import { useState, useEffect, useRef } from 'react';

export const GROUP_OPTIONS = [
  { value: 'family', labelKey: 'family' },
  { value: 'plant_type', labelKey: 'plantType' },
  { value: 'watering', labelKey: 'watering' },
  { value: 'light', labelKey: 'light' },
  { value: 'humidity', labelKey: 'humidity' },
  { value: 'temperature', labelKey: 'temperature' },
  { value: 'toxicity', labelKey: 'toxicity' },
  { value: 'environment', labelKey: 'environment' },
  { value: 'growth_rate', labelKey: 'growthRate' },
];

// Flat (list-ordering) sort keys — everything else is a grouping field.
// `date_added` is a flat newest→oldest ordering (NOT a day-bucket grouping).
export const FLAT_SORT_KEYS = ['name_asc', 'name_desc', 'date_added'];
const isFlatKey = (s) => FLAT_SORT_KEYS.includes(s);

const PER_PAGE_OPTIONS = [12, 24, 48];
export const LIST_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const GROUP_BUCKETS = {
  // Watering uses ORDERED pattern matching so rich AI-generated phrases
  // (e.g. "allow soil to dry between waterings") all collapse into a small
  // set of buckets. First matching bucket wins, so put more specific
  // patterns higher up.
  watering: [
    // Put "Low" before "Frequent" so "infrequent" / "deeply but infrequently"
    // don't get caught by the "frequent" substring inside Frequent.
    { label: 'Low', patterns: ['sparingly', 'drought', 'every 2-3 week', 'every 2 week', 'every 3 week', 'minimal water', 'succulent', 'very little', 'infrequent', 'low water', 'water once a month', 'water deeply but', 'tolerates dry', 'xerophyt'] },
    { label: 'Frequent', patterns: ['frequent', 'daily', 'boggy', 'constantly wet', 'high water', 'wet soil', 'wet feet', 'every day', 'twice a week', '2-3 times a week', 'multiple times a week', 'do not let dry'] },
    { label: 'Keep moist', patterns: ['consistently moist', 'evenly moist', 'keep moist', 'keep soil moist', 'stay moist', 'moist soil', 'always moist', 'continuously moist'] },
    { label: 'Allow to dry', patterns: ['allow soil to dry', 'allow to dry', 'let dry', 'let soil dry', 'dry between water', 'dry out between', 'fully dry', 'completely dry', 'top inch', 'top 1 inch', 'top 1-2 inch', 'top 2 inch', 'top couple of inch', 'top few inch', 'top of soil', 'topsoil', 'when dry', 'feels dry', 'is dry', 'partially dry', 'mostly dry'] },
    { label: 'Moderate', patterns: ['moderate', 'average', 'regular water', 'weekly', 'medium water', 'water when', 'water as needed'] },
  ],

  light: [
    // Bright indirect is the single most common AI phrase; put it high
    // so it doesn't get shadowed by the "bright" or "indirect" substrings.
    { label: 'Bright Indirect', patterns: ['bright indirect', 'bright, indirect', 'bright but indirect', 'bright, filtered', 'bright filtered', 'bright but no direct', 'bright but not direct', 'indirect bright', 'indirect, bright'] },
    { label: 'Full Sun', patterns: ['full sun', 'direct sun', 'direct sunlight', 'south-facing', 'south facing', 'south window', '6+ hours', '6 hours of direct', 'six hours of direct', 'full daylight', 'full light'] },
    { label: 'Low Light', patterns: ['low light', 'tolerates low', 'shade', 'shady', 'shaded', 'dim', 'dark corner', 'north-facing', 'north facing', 'north window'] },
    { label: 'Medium Light', patterns: ['medium light', 'medium indirect', 'moderate light', 'moderate indirect', 'partial', 'dappled', 'filtered light', 'filtered sunlight', 'east-facing', 'east facing', 'east window', 'west-facing', 'west facing', 'west window'] },
    // Pure "indirect" / "bright" fallbacks (catch lone keywords)
    { label: 'Bright Indirect', patterns: ['indirect light', 'indirect sunlight', 'indirect'] },
    { label: 'Full Sun', patterns: ['bright direct', 'bright sun', 'full bright', 'lots of light'] },
  ],

  humidity: [
    // Check Low first — "low humidity" should not fall into High via the
    // word "humidity" containing "humid". Use only specific phrases.
    { label: 'Low', patterns: ['low humidity', 'tolerates dry', 'dry air', 'arid', 'desert-like', 'desert', 'tolerates low', 'low humid'] },
    { label: 'High', patterns: ['high humidity', 'high humid', 'tropical humid', 'very humid', 'extra humid', 'mist', 'pebble tray', 'humidifier', 'prefers high', 'bathroom', '60%', '70%', '80%'] },
    { label: 'Moderate', patterns: ['moderate', 'average', 'normal household', 'household humidity', 'household humid', 'average household', 'standard household', '40%', '50%', 'medium humidity', 'household'] },
  ],

  temperature: [
    // Order: Warm (tropical/hot keywords) → Cool (cool/cold + low ranges)
    // → Moderate (the catch-all for typical houseplant ranges like 65-80°F).
    // Use hyphenated range prefixes (e.g. "85-") rather than bare temperatures
    // (e.g. "5°C") so the substring matcher doesn't accidentally match "27°C".
    { label: 'Warm', patterns: ['tropical', 'warm', 'hot', 'above 80', 'above 75', 'heat-loving', 'heat loving', '80-9', '85-', '90-', '95-', '70-9', '75-9', '75-95', '80-95'] },
    { label: 'Cool', patterns: ['cool', 'cold', 'chilly', '40-', '45-', '50-', '55-', 'below 60', 'below 65', 'frost-tender', 'down to 4', 'down to 3', 'down to 5'] },
    { label: 'Moderate', patterns: ['moderate', 'average', 'room temperature', 'room temp', '60-', '62-', '65-', '68-', '70-', '72-', '15-', '16-', '17-', '18-', '20-', '21-', 'normal'] },
  ],

  toxicity: [
    // Non-toxic FIRST so "non-toxic" doesn't trigger the "toxic" bucket.
    { label: 'Non-Toxic', patterns: ['non-toxic', 'non toxic', 'nontoxic', 'safe for pet', 'pet safe', 'pet-safe', 'non poisonous', 'non-poisonous', 'harmless', 'edible', 'safe for cat', 'safe for dog', 'not toxic', 'not poisonous'] },
    { label: 'Toxic', patterns: ['toxic', 'poisonous', 'harmful', 'irritant', 'dangerous', 'do not ingest', 'mildly toxic', 'severely toxic', 'toxic if ingest', 'oxalate'] },
  ],

  growth_rate: [
    { label: 'Slow', patterns: ['slow', 'gradual', 'minimal growth'] },
    { label: 'Fast', patterns: ['fast', 'rapid', 'quick', 'vigorous', 'aggressive grower', 'prolific'] },
    { label: 'Moderate', patterns: ['moderate', 'medium', 'average', 'steady'] },
  ],

  // Environment / terrarium requirement. Order matters: check the most
  // specific phrase ("conservatory required") before generic substrings.
  environment: [
    { label: 'Terrarium/Conservatory required', patterns: ['conservatory required', 'terrarium or conservatory', 'terrarium required', 'conservatory or terrarium', 'requires terrarium', 'requires conservatory', 'must be in a terrarium', 'must be kept in a terrarium', 'wardian case'] },
    { label: 'Terrarium preferred', patterns: ['terrarium preferred', 'prefers terrarium', 'terrarium recommended', 'benefits from terrarium', 'better in a terrarium', 'thrives in terrarium'] },
    { label: 'Terrarium not necessary', patterns: ['standard household', 'household', 'open room', 'normal home', 'normal house', 'no terrarium', 'terrarium not needed', 'terrarium not necessary', 'does not need terrarium', 'thrives in normal', 'average home'] },
  ],
};

function titleCaseLabel(s) {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function bucketValue(field, raw) {
  if (!raw) return 'Not Specified';
  // Normalize: trim, lowercase, strip trailing punctuation, collapse whitespace
  const lower = raw.trim().toLowerCase().replace(/[\s.;,!]+$/g, '').replace(/\s+/g, ' ');
  const buckets = GROUP_BUCKETS[field];
  if (!buckets) return lower ? titleCaseLabel(lower) : 'Not Specified';

  // Array form (ordered list of pattern groups). First match wins.
  // Anything that doesn't match any pattern collapses into "Other" so the
  // group list stays small no matter what the AI generates.
  if (Array.isArray(buckets)) {
    for (const { label, patterns } of buckets) {
      for (const p of patterns) {
        if (lower.includes(p)) return label;
      }
    }
    return 'Other';
  }

  // Object form (legacy): direct lookup + substring fallback
  if (buckets[lower]) return buckets[lower];
  for (const [key, label] of Object.entries(buckets)) {
    if (lower.includes(key)) return label;
  }
  return lower ? titleCaseLabel(lower) : 'Not Specified';
}

// ---- Unified comparators (iter 142) ----------------------------------------
// A single comparator per sort key so the primary sort AND the "Then by"
// secondary sort share identical ordering logic. Flat keys order the list
// directly; grouping-field keys order by the plant's bucket label (used as a
// tie-breaker / inner order, never to re-group).
function nameCmp(a, b) {
  return (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' });
}
function dateMs(p) {
  const d = p.created_at ? new Date(p.created_at) : null;
  return d && !isNaN(d) ? d.getTime() : null;
}
function cmpByKey(key, a, b) {
  switch (key) {
    case 'name_asc': return nameCmp(a, b);
    case 'name_desc': return -nameCmp(a, b);
    case 'date_added': {
      const av = dateMs(a);
      const bv = dateMs(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // undated plants sink to the bottom
      if (bv == null) return -1;
      return bv - av;             // newest first
    }
    default: {
      // Grouping field used as an ordering key — compare bucket labels,
      // "Not Specified" always last.
      const al = bucketValue(key, a.care_details?.[key] || '');
      const bl = bucketValue(key, b.care_details?.[key] || '');
      if (al === 'Not Specified' && bl !== 'Not Specified') return 1;
      if (bl === 'Not Specified' && al !== 'Not Specified') return -1;
      return al.localeCompare(bl);
    }
  }
}
export function useViewMode(filteredPlants, searchQuery, setSearchQuery) {
  // iter 71 — unified sort/group model. `sortBy` is the primary organization
  // key: either a flat sort (`name_asc` / `name_desc`) or a grouping field
  // (`family`, `plant_type`, `watering`, …). Persisted in localStorage.
  // `thenBy` is an optional secondary key (session-only per user request) —
  // when set alongside a name/date sort it PROMOTES to grouping (e.g.
  // `Name (A→Z) → then by Family` renders Family groups with A-Z inside).
  const [sortBy, _setSortBy] = useState(() => {
    const persisted = localStorage.getItem('plant_sort_by');
    if (persisted) return persisted;
    // First-run migration from the legacy 3-key model (viewMode + sortAsc +
    // groupBy) so returning users don't get their preferences reset.
    const legacyMode = localStorage.getItem('plant_view_mode');
    const legacyAsc = localStorage.getItem('plant_sort_asc');
    const legacyGroup = localStorage.getItem('plant_group_by');
    if (legacyMode === 'grouped' && legacyGroup) return legacyGroup;
    if (legacyMode === 'alphabetical' && legacyAsc === 'false') return 'name_desc';
    return 'name_asc';
  });
  const [thenBy, setThenBy] = useState(null); // session only, resets each visit
  const [cardSize, setCardSize] = useState(() => localStorage.getItem('plant_card_size') || 'normal');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const groupDropdownRef = useRef(null);
  // Legacy state kept as a derived source-of-truth mirror so any downstream
  // code that still reads viewMode/sortAsc/groupBy keeps working. They are
  // written by an effect below whenever sortBy changes.
  const [viewMode, setViewMode] = useState(() => {
    const s = localStorage.getItem('plant_sort_by');
    if (!s) return localStorage.getItem('plant_view_mode') || 'alphabetical';
    return isFlatKey(s) ? 'alphabetical' : 'grouped';
  });
  const [sortAsc, setSortAsc] = useState(() => {
    const s = localStorage.getItem('plant_sort_by');
    if (!s) return localStorage.getItem('plant_sort_asc') !== 'false';
    return s !== 'name_desc';
  });
  const [groupBy, setGroupBy] = useState(() => {
    const s = localStorage.getItem('plant_sort_by');
    if (!s) return localStorage.getItem('plant_group_by') || 'watering';
    return isFlatKey(s) ? (localStorage.getItem('plant_group_by') || 'watering') : s;
  });

  const [perPage, setPerPage] = useState(() => {
    const saved = localStorage.getItem('plant_per_page');
    return saved ? parseInt(saved, 10) : 12;
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Persist ONLY sortBy — thenBy is deliberately session-only per user request.
  useEffect(() => { localStorage.setItem('plant_sort_by', sortBy); }, [sortBy]);
  useEffect(() => { localStorage.setItem('plant_card_size', cardSize); }, [cardSize]);
  useEffect(() => { localStorage.setItem('plant_per_page', perPage.toString()); }, [perPage]);

  // Mirror sortBy → legacy state so any code still reading viewMode/sortAsc/
  // groupBy (there's currently only one consumer, LibraryPage.js, but this
  // keeps the door open) stays consistent without needing a full rewrite.
  useEffect(() => {
    if (isFlatKey(sortBy)) {
      setViewMode('alphabetical');
      setSortAsc(sortBy !== 'name_desc');
    } else {
      setViewMode('grouped');
      setGroupBy(sortBy);
    }
  }, [sortBy]);

  // Adjust perPage when switching between list and card views
  useEffect(() => {
    if (cardSize === 'list') {
      if (!LIST_PER_PAGE_OPTIONS.includes(perPage)) {
        setPerPage(25);
      }
    } else {
      if (!PER_PAGE_OPTIONS.includes(perPage)) {
        setPerPage(12);
      }
    }
  }, [cardSize]);

  // Reset page on filter changes
  useEffect(() => { setCurrentPage(1); }, [searchQuery, viewMode, groupBy, perPage]);

  // Close group dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target)) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Effective render mode derivation (iter 142 — true secondary sort).
  // - Flat primary (name_asc / name_desc / date_added) → one flat list,
  //   ordered by the primary, with `thenBy` as a genuine tie-breaker.
  // - Grouping primary (family, watering, …) → grouped view; within each
  //   group, order by `thenBy` (or Name A→Z when no secondary is set).
  // `thenBy` NEVER re-groups the view — it only orders.
  const isSortByFlat = isFlatKey(sortBy);
  const effectiveGroupField = isSortByFlat ? null : sortBy;
  const effectiveInnerAsc = true; // retained for backwards-compat consumers
  const effectiveViewMode = effectiveGroupField ? 'grouped' : 'alphabetical';
  const showDateAdded = sortBy === 'date_added' || thenBy === 'date_added';

  // Flat list honoring the primary sort + secondary tie-breaker. A final
  // name compare keeps ordering stable/deterministic.
  const sortedPlants = (() => {
    const base = [...filteredPlants];
    base.sort((a, b) => {
      let c = cmpByKey(sortBy, a, b);
      if (c !== 0) return c;
      if (thenBy) { c = cmpByKey(thenBy, a, b); if (c !== 0) return c; }
      return nameCmp(a, b);
    });
    return base;
  })();

  // Pagination derived values
  const totalComplete = filteredPlants.length;
  const totalPages = Math.max(1, Math.ceil(totalComplete / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * perPage;
  const paginatedPlants = sortedPlants.slice(startIndex, startIndex + perPage);

  // Legacy setter kept for any straggling references. The one place it was
  // used (LibraryPage's old A-Z / Grouped toggle) has been replaced by
  // setSortByWithScroll below.
  function setViewModeWithScroll(mode) {
    if (mode === 'alphabetical' && viewMode === 'alphabetical') {
      setSortAsc(prev => !prev);
    } else {
      setViewMode(mode);
      if (setSearchQuery) setSearchQuery('');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function setGroupByWithScroll(value) {
    setGroupBy(value);
    setShowGroupDropdown(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Unified setter for the new sort/group model (iter 71).
  function setSortByWithScroll(value) {
    _setSortBy(value);
    if (setSearchQuery) setSearchQuery('');
    // Auto-clear a thenBy that would now conflict (same as sortBy) — safer
    // than silently showing a self-referencing grouping.
    if (thenBy === value) setThenBy(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function setThenByWithScroll(value) {
    setThenBy(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function getGroupedPlants() {
    // Group strictly by the primary grouping field; order within each group by
    // the secondary `thenBy` (or Name A→Z by default). `thenBy` is a sort,
    // never a sub-grouping.
    const field = effectiveGroupField;
    if (!field) return [];
    const innerKey = thenBy || 'name_asc';
    const groups = {};
    for (const plant of filteredPlants) {
      const value = bucketValue(field, plant.care_details?.[field] || '');
      if (!groups[value]) groups[value] = [];
      groups[value].push(plant);
    }
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Not Specified') return 1;
      if (b === 'Not Specified') return -1;
      return a.localeCompare(b);
    });
    for (const key of sortedKeys) {
      groups[key].sort((a, b) => {
        const c = cmpByKey(innerKey, a, b);
        return c !== 0 ? c : nameCmp(a, b);
      });
    }
    return sortedKeys.map(key => ({ label: key, plants: groups[key] }));
  }

  function setCurrentPageWithScroll(page) {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return {
    // Unified iter 71 model
    sortBy, setSortBy: setSortByWithScroll,
    thenBy, setThenBy: setThenByWithScroll,
    effectiveGroupField, effectiveInnerAsc, effectiveViewMode, showDateAdded,
    // Legacy exports (still used inside this file's own derivations and by
    // any older reference site that hasn't migrated yet)
    viewMode: effectiveViewMode, setViewMode: setViewModeWithScroll, sortAsc,
    cardSize, setCardSize,
    groupBy, setGroupBy: setGroupByWithScroll,
    showGroupDropdown, setShowGroupDropdown, groupDropdownRef,
    perPage, setPerPage, currentPage, setCurrentPage: setCurrentPageWithScroll,
    PER_PAGE_OPTIONS, LIST_PER_PAGE_OPTIONS,
    totalComplete, totalPages, safePage, startIndex, paginatedPlants,
    getGroupedPlants,
  };
}
